import { api } from "@convex/_generated/api";
import { useConvexAuth, useQuery } from "convex/react";

/**
 * Live credit balance — Convex-backed, replacing the old SWR + Supabase
 * realtime-channel polling pair. Reads the server-computed
 * `availableCredits` query directly ("D9 FINAL", docs/superpowers/specs/
 * 2026-09-16-supabase-to-convex-migration-design.md) — no client-side
 * filtering or summing.
 *
 * Gated on `useConvexAuth`, and that gate is load-bearing rather than an
 * optimisation. `availableCredits` calls `requireIdentity`, which throws
 * `ConvexError({ code: "UNAUTHENTICATED" })`. Convex only pauses its socket
 * once `ConvexProviderWithAuth` has called `client.setAuth`, and it calls
 * that only after Supabase has delivered a session — so an ungated query
 * subscribed at mount races the websocket handshake, and losing that race
 * throws into the nearest error boundary. The retry that follows once the
 * token lands never renders, because the boundary has already swallowed the
 * tree. `"skip"` holds the subscription until Convex reports an identity the
 * server has accepted.
 *
 * The same gate is written with `<Authenticated>`/`<AuthLoading>` where the
 * consumer is a self-contained widget (CreditsBreakdown, NotificationBell).
 * It lives in the hook here because this balance is read from a dozen
 * components nested deep inside forms and modals, where wrapping each call
 * site would change layout for no benefit.
 *
 * The display is advisory, not authoritative either way: `consumeCreditsFEFO`
 * is a `serviceMutation` a browser cannot call, and it reads its own clock
 * and batches before planning a spend, so a stale display can never cause
 * an overspend.
 */
export function useCreditsBalance() {
  const { isAuthenticated, isLoading: isAuthLoading } = useConvexAuth();
  const summary = useQuery(
    api.credits.availableCredits,
    isAuthenticated ? {} : "skip",
  );

  return {
    credits: summary?.total ?? null,
    // Loading while auth is still settling, and while the query itself is in
    // flight. Once auth has settled as unauthenticated this reports `false`
    // with `credits: null`, so consumers render "N/A" instead of spinning
    // forever on a query that will never be issued.
    isLoading: isAuthLoading || (isAuthenticated && summary === undefined),
    // Convex surfaces query failures by throwing to the nearest error
    // boundary rather than returning them inline, so there is no error
    // state for `useQuery` to report here. Kept in the return shape only
    // so the consumers that destructure `error` (CreditsDisplay,
    // BadgeCreditsBalance) keep compiling — both already treat a falsy
    // value as "no error".
    error: undefined,
    // Convex is reactive: every write that can change the balance (spend,
    // allocation, refund, grant, expiry) pushes to subscribers on its own,
    // so there is nothing to manually revalidate. Kept as a no-op only so
    // the consumers that call `mutate()` (e.g. after a top-up) still
    // compile.
    mutate: () => {},
  };
}
